import { type ReactNode } from 'react';
import { type SupportedLocaleMap } from '@zep-us/i18n';
import { Trans, useTranslation } from 'next-i18next';
import { useLocale } from '@/hooks';
import { DialogBody, DialogFooter, DialogHeader, ResponsiveDialog } from '@/ui_v3';
import { FEATURE_UPSELL_CONFIG, type FeatureUpsellFeature, type GuideLocale, resolveFeatureUpsellImageSrc } from './featureUpsell.config';
import { UpgradeButton } from './UpgradeButton';
import { UpsellFeatureIntro } from './UpsellFeatureIntro';
import { useUpgradeToPro } from './useUpgradeToPro';

/**
 * 번역 시트를 거치지 않은 문구를 그대로 넣고 싶을 때 쓰는 덮어쓰기.
 *
 * 같은 기능이라도 부르는 자리에 따라 주어가 달라진다(퀴즈 숨기기 ↔ 폴더 숨기기). 새 문구가
 * 아직 시트에 없으면 호출부가 하드코딩한 값을 여기로 넘기고, 시트 등록 뒤에 키로 옮긴다.
 * 이미지·업그레이드 버튼·트래킹은 `feature` 설정을 그대로 쓴다.
 */
export interface FeatureUpsellCopy {
  modalTitle: string;
  title: ReactNode;
  description: ReactNode;
  notices: ReactNode[];
  footnote?: ReactNode;
  /** 가이드 이미지도 자리마다 다르면 함께 덮어쓴다. 없는 로케일은 영어로 접힌다. */
  images?: SupportedLocaleMap<GuideLocale>;
}
interface Props {
  feature: FeatureUpsellFeature;
  copy?: FeatureUpsellCopy;
  onClose?: VoidFunction;
}
export const FeatureUpsellModal = ({
  feature,
  copy,
  onClose
}: Props) => {
  const {
    t
  } = useTranslation(['build']);
  const {
    locale
  } = useLocale();
  const upgradeToPro = useUpgradeToPro();
  const config = FEATURE_UPSELL_CONFIG[feature];
  const modalTitle = copy?.modalTitle ?? t(config.modalTitleKey);
  const handleUpgrade = () => upgradeToPro('click_feature_upselling_upgrade', config.from);

  // 고정 높이 모달. DialogContent 는 flex flex-col 이므로, 이 모달에 한해
  // 헤더/푸터를 shrink-0 으로 고정하고 본문을 flex-1 min-h-0 로 스크롤시킨다.
  // (전역 dialog 프리미티브는 건드리지 않고 여기서만 국소 적용)
  return <ResponsiveDialog onClose={onClose} size="lg" className="max-mobile-lg:!h-[calc(90dvh-env(safe-area-inset-top))] mobile-lg:!h-[642px] mobile-lg:!w-[540px]" data-sentry-element="ResponsiveDialog" data-sentry-component="FeatureUpsellModal" data-sentry-source-file="FeatureUpsellModal.tsx">
      <DialogHeader className="shrink-0" title={modalTitle} hasCloseButton onClose={onClose} data-sentry-element="DialogHeader" data-sentry-source-file="FeatureUpsellModal.tsx" />
      <DialogBody className="h-auto min-h-0 flex-1 overflow-y-scroll [scrollbar-gutter:stable]" data-sentry-element="DialogBody" data-sentry-source-file="FeatureUpsellModal.tsx">
        <UpsellFeatureIntro imageSrc={resolveFeatureUpsellImageSrc(copy?.images ?? config.images, locale)} imageAlt={modalTitle} title={copy?.title ?? <Trans t={t} i18nKey={config.titleKey} components={{
        br: <br />,
        Strong: <strong className="text-strong" />
      }} />} description={copy?.description ?? t(config.descriptionKey)} notices={copy?.notices ?? config.noticeKeys.map(key => t(key))} footnote={copy ? copy.footnote : config.footnoteKey ? t(config.footnoteKey) : undefined} data-sentry-element="UpsellFeatureIntro" data-sentry-source-file="FeatureUpsellModal.tsx" />
      </DialogBody>
      <DialogFooter className="shrink-0 px-lg pb-lg pt-xs mobile-lg:px-lg mobile-lg:pb-lg mobile-lg:pt-xs" data-sentry-element="DialogFooter" data-sentry-source-file="FeatureUpsellModal.tsx">
        <UpgradeButton onClick={handleUpgrade} data-sentry-element="UpgradeButton" data-sentry-source-file="FeatureUpsellModal.tsx" />
      </DialogFooter>
    </ResponsiveDialog>;
};